import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const SIEVE_BEND_CONTENT: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "SieveBendScreens",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Wedge Wire Screens",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Static Sieve Bend Screens",
      lead: "Curved cross-flow profile-wire panels engineered for unpowered gravity separation, rapid surface dewatering, and precise solid particle retention across industrial processing streams.",
      secondaryCta: {
        label: "Wedge Wire Overview",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
        alt: "Curved stainless steel sieve bend screen panel for solid-liquid dewatering",
      },
      cardNote: "Slot apertures 0.25 to 3.0 mm",
      cardLink: {
        label: "Request a Quote",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Slot Range", value: "0.25 – 3.0 mm" },
        { label: "Standard Arcs", value: "45° / 60° / 120°" },
        { label: "Operating Mode", value: "Static Gravity Flow" },
        { label: "Alloy Grades", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Static Cross-Flow Hydro-Extraction",
      heading: "Continuous Gravity Dewatering without Mechanical Drives",
      paragraphs: [
        "Sieve bend screens, commonly recognized across mineral, pulp, and wastewater processing as DSM screens, operate on cross-flow hydrodynamic principles. Slurry feeds tangential to the top curve and accelerates downward across perpendicular V-shaped profile wires. The leading edge of each wire slices a thin boundary layer of liquid away from the slurry body, directing it through the widening slot aperture while dewatered solids continue down the concave face.",
        "Because the curved geometry relies exclusively on gravity head pressure and inertia, the screen operates without rotary drives, vibrating exciters, or electrical connections. This eliminates rotating mechanical wear components, lowers continuous operating utility expenses, and maintains consistent volumetric liquid throughput.",
        "Each screen panel is manufactured by precision resistance fusion welding of cold-rolled stainless steel V-wires directly onto transverse support ribs. Standard arc curvatures of 45, 60, and 120 degrees are built in AISI 304 and AISI 316L, supplied either as bare replacement panels or complete modular units with feed boxes and overflow weir distribution.",
      ],
      keyPoints: [
        "Pure static gravity operation with zero drive motors, eliminating electrical draw and mechanical wear",
        "Continuous non-clogging open area across curved profile strips liquid through slots while solids slide down",
        "Supplied as replacement curved panels or complete housings with weir distributor feed boxes and splash covers",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
        alt: "Industrial stainless steel curved sieve bend screen panel for solid liquid dewatering",
      },
    },
    specTable: {
      heading: "Standard Sieve Bend Engineering Parameters",
      intro: "Representative dimensional configurations for static curved screen panels in stainless steel alloys.",
      columns: [
        { key: "slot", label: "Slot Aperture (mm)" },
        { key: "arcAngle", label: "Standard Arc Angle", align: "right" },
        { key: "radius", label: "Nominal Radius (mm)", align: "right" },
        { key: "width", label: "Panel Width (mm)", align: "right" },
        { key: "metallurgy", label: "Standard Alloys", align: "right" },
      ],
      rows: [
        { slot: "0.25 mm", arcAngle: "45° / 60°", radius: "1016 mm", width: "300 – 1500 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "0.50 mm", arcAngle: "45° / 60°", radius: "1016 mm", width: "300 – 2000 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "0.75 mm", arcAngle: "45° / 60° / 120°", radius: "1016 mm / 1524 mm", width: "500 – 2000 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "1.00 mm", arcAngle: "45° / 60° / 120°", radius: "1016 mm / 1524 mm", width: "500 – 2200 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "1.50 mm", arcAngle: "45° / 60°", radius: "1524 mm", width: "600 – 2400 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "2.00 mm", arcAngle: "45° / 60°", radius: "1524 mm", width: "600 – 2400 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "3.00 mm", arcAngle: "45° / 60°", radius: "1524 mm / 2032 mm", width: "600 – 2500 mm", metallurgy: "AISI 304 / 316L" },
      ],
      caption: "Custom arc radii, camber angles, side clamp bars, and weir distributor housings available upon technical specification.",
    },
    features: {
      heading: "Design Attributes & Fabrication Options",
      intro: "Structural advantages of MutualFilter resistance-welded static curved screen assemblies.",
      items: [
        {
          title: "Hydrodynamic Shear Dewatering",
          text: "Horizontal profile wires oriented across the slurry path shear off micro-layers of water cleanly without blinding the aperture.",
        },
        {
          title: "Widening Inward Slot Geometry",
          text: "The continuous V-shaped wire profile widens inward, ensuring particles that pass the surface edge clear without entrapment.",
        },
        {
          title: "Rigid Cross-Rib Resistance Welding",
          text: "Automated fusion welding at every intersection produces structural rigidity capable of handling heavy continuous slurry loads.",
        },
        {
          title: "Reversible Curved Panel Mounting",
          text: "Symmetrical panel designs allow end-for-end reversal to extend operating life when initial wear concentrates on the top impact zone.",
        },
      ],
    },
    related: {
      heading: "Related Wedge Wire Screen Architectures",
      intro: "Compare static sieve bends with flat panels, curved screens, cylinders, and screen tubes in the profile wire family.",
      cards: [
        {
          title: "Curved Screens",
          text: "Radial profile wire panels curved to custom radii for vibrating frames, trommel linings, and curved sumps.",
          to: "/CurvedScreensPage",
          badge: "Curved Sibling",
          linkLabel: "View Curved Screens",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
            alt: "Curved wedge wire screen panel for industrial classification",
          },
        },
        {
          title: "Flat Panel Screens",
          text: "Rigid flat screen panels with perimeter framing for vibratory sizing, wash screening, and static underdrains.",
          to: "/FlatPanelScreensPage",
          badge: "Flat Sibling",
          linkLabel: "View Flat Panels",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "Flat wedge wire screen panel with perimeter frame",
          },
        },
        {
          title: "Cylindrical Screens",
          text: "Continuous slot wedge wire cylinders engineered for rotary trommels, intake baskets, and reactor internals.",
          to: "/CylindricalScreensPage",
          badge: "Cylindrical Sibling",
          linkLabel: "View Cylinders",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "Cylindrical wedge wire screen assemblies",
          },
        },
        {
          title: "Screen Tubes",
          text: "Longitudinal and circumferential slot filter tubes for deep well water intake and process straining.",
          to: "/ScreenTubesPage",
          badge: "Tubular Sibling",
          linkLabel: "View Screen Tubes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Stainless steel wedge wire screen tubes",
          },
        },
        {
          title: "Filter Nozzles",
          text: "Precision slotted wedge wire underdrain nozzles for ion exchange vessels, sand filters, and carbon towers.",
          to: "/FilterNozzlesPage",
          badge: "Underdrain Sibling",
          linkLabel: "View Filter Nozzles",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "Stainless steel wedge wire filter nozzles",
          },
        },
        {
          title: "Wedge Wire Screen Overview",
          text: "The comprehensive profile wire product family covering flat panels, cylinders, tubes, and curved DSM screens.",
          to: "/WedgeWireScreenPage",
          badge: "Parent Category",
          linkLabel: "View Wedge Wire Family",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
            alt: "Wedge wire screen manufacturing overview",
          },
        },
      ],
    },
    cta: {
      id: "sieve-bend-cta",
      eyebrow: "Static Separation Engineering",
      heading: "Inquire About Sieve Bend Screen Specifications",
      lead: "Contact our technical engineering team for slot width sizing, arc angle selection, or replacement curved panel dimensions.",
      secondaryLabel: "Return to Wedge Wire Screens",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Standard alloys include AISI 304 and AISI 316L. Material test certificates and dimensional inspection records available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "SieveBendScreens",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "楔形丝筛网系列",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "静态弧形筛板系统 (Sieve Bend)",
      lead: "精密弯曲横流楔形丝筛面，专为无动力重力自流固液分离、快速表面脱水与固体颗粒精准截留而设计，广泛服务于矿业、淀粉与工业水处理工艺。",
      secondaryCta: {
        label: "楔形丝筛网总览",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
        alt: "工业不锈钢重力脱水弧形筛板（DSM筛）",
      },
      cardNote: "缝隙公差范围 0.25 至 3.0 mm",
      cardLink: {
        label: "获取工程报价",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "缝隙范围", value: "0.25 – 3.0 mm" },
        { label: "标准弧度", value: "45° / 60° / 120°" },
        { label: "进料形式", value: "静态重力自流" },
        { label: "常用材质", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "静态横流自流分离机制",
      heading: "无动力机械驱动的高效固液重力脱水",
      paragraphs: [
        "静态弧形筛（行业中常称为 DSM 筛）基于横流流体动力学原理运行。悬浮矿浆或固液混合物流经溢流堰布料箱后，沿切线方向均匀冲击进入弯曲筛面，并在重力加速度作用下顺着与横向 V 型丝垂直的方向向下高速滑动。每根型材丝的锋利迎流边缘像刀刃一样从物料流底层薄薄地切削剥离一层液膜，引导滤液顺畅穿过外窄内宽的自清洁梯形缝隙，而脱水后的固体物料则沿凹面加速滑出排料端。",
        "由于整个分离过程完全依靠重力势能与物料自流动能，筛体内部没有任何电动机、减速箱或振动激振器。这种纯静态运行方式彻底杜绝了旋转运动副的机械磨损与能耗开销，大幅降低了连续运行的维护停机频次，并在高处理量工况下保持稳定通量。",
        "MutualFilter 制造的弧形筛板采用全自动电阻熔焊工艺，将冷轧不锈钢梯形丝与纵向支撑扁钢每个交叉点一体熔合。标准弧面弧度规格包括 45 度、60 度与 120 度，材质覆盖 AISI 304 与 AISI 316L，既支持标准尺寸的备件筛板替换，也可提供配备溢流布料箱与防溅盖板的成套筛箱壳体。",
      ],
      keyPoints: [
        "全重力自流静态运行，无电机驱动与运动部件，彻底降低电力消耗与维护故障率",
        "弧形截面连续自清洁开孔结构，切刃效应剥离液膜穿过筛缝，固体颗粒顺畅滑落",
        "支持定制替换筛板及成套筛箱壳体总成，配套溢流堰布料箱、防溅盖板与安装接口",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
        alt: "工业不锈钢重力脱水弧形筛板（DSM筛）",
      },
    },
    specTable: {
      heading: "标准静态弧形筛工程技术参数表",
      intro: "代表性不锈钢静态弧形筛板规格参数，符合工业固液分离应用制造规范。",
      columns: [
        { key: "slot", label: "筛缝宽度 (mm)" },
        { key: "arcAngle", label: "标准圆弧角度", align: "right" },
        { key: "radius", label: "标准曲率半径 (mm)", align: "right" },
        { key: "width", label: "筛面宽度 (mm)", align: "right" },
        { key: "metallurgy", label: "标准合金材质", align: "right" },
      ],
      rows: [
        { slot: "0.25 mm", arcAngle: "45° / 60°", radius: "1016 mm", width: "300 – 1500 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "0.50 mm", arcAngle: "45° / 60°", radius: "1016 mm", width: "300 – 2000 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "0.75 mm", arcAngle: "45° / 60° / 120°", radius: "1016 mm / 1524 mm", width: "500 – 2000 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "1.00 mm", arcAngle: "45° / 60° / 120°", radius: "1016 mm / 1524 mm", width: "500 – 2200 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "1.50 mm", arcAngle: "45° / 60°", radius: "1524 mm", width: "600 – 2400 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "2.00 mm", arcAngle: "45° / 60°", radius: "1524 mm", width: "600 – 2400 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "3.00 mm", arcAngle: "45° / 60°", radius: "1524 mm / 2032 mm", width: "600 – 2500 mm", metallurgy: "AISI 304 / 316L" },
      ],
      caption: "支持定制曲率半径、边框包角固定条、成套不锈钢外壳与布料箱总成。",
    },
    features: {
      heading: "结构设计优势与制造工艺",
      intro: "MutualFilter 全焊接静态弧形筛在严苛工业工艺中的关键工程特性。",
      items: [
        {
          title: "流体动力水力剪切脱水",
          text: "横向梯形丝垂直于流向排列，迎流切面高效剥离物料底部液膜，有效防止筛孔盲死与结团。",
        },
        {
          title: "内宽自释放梯形缝隙",
          text: "V 型型线形成外窄内宽微米级开孔通道，通过前沿切口的固体颗粒随流体顺畅排出，不卡堵。",
        },
        {
          title: "全自动交叉点电阻熔焊",
          text: "支撑筋条与型材丝每一交织点熔融接合，具备高刚性与抗弯扭强度，耐受长期重负荷矿浆冲刷。",
        },
        {
          title: "对称结构支持 180° 翻转调头",
          text: "上下端对称设计的筛板可定期翻转 180 度安装使用，平衡进料端局部冲刷磨损，延长使用寿命。",
        },
      ],
    },
    related: {
      heading: "相关楔形丝筛网产品系列",
      intro: "对比静态弧形筛与平面筛板、曲面筛、滤筒及滤管在工业固液分离中的应用特点。",
      cards: [
        {
          title: "曲面筛网 (Curved Screens)",
          text: "按定制半径精密弯曲的型线筛面，适配振动筛框、滚筒筛内衬与弧形槽体。",
          to: "/CurvedScreensPage",
          badge: "曲面同类",
          linkLabel: "查看曲面筛网",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
            alt: "用于工业分选的曲面楔形丝筛板",
          },
        },
        {
          title: "平面筛板 (Flat Panel Screens)",
          text: "配装刚性外框的平面型线筛网，专用于振动脱水筛、洗选设备及下排水滤床。",
          to: "/FlatPanelScreensPage",
          badge: "平面同类",
          linkLabel: "查看平面筛板",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "带外框的工业不锈钢平面楔形丝筛板",
          },
        },
        {
          title: "圆柱滤筒 (Cylindrical Screens)",
          text: "连续缝隙圆筒形滤网组件，广泛应用于旋转滚筒过滤器、取水篮及反应器内件。",
          to: "/CylindricalScreensPage",
          badge: "圆柱同类",
          linkLabel: "查看圆柱滤筒",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "多规格楔形丝圆柱筛筒组件",
          },
        },
        {
          title: "滤管滤芯 (Screen Tubes)",
          text: "具备纵向或环向连续缝隙的刚性滤管，用于深井取水过滤与工艺介质精细拦截。",
          to: "/ScreenTubesPage",
          badge: "管状同类",
          linkLabel: "查看滤管滤芯",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "不锈钢楔形丝筛管与深井滤水管",
          },
        },
        {
          title: "滤水帽 (Filter Nozzles)",
          text: "精密微孔缝隙式排水水帽，专用于离子交换柱、石英砂过滤器与活性炭吸附塔。",
          to: "/FilterNozzlesPage",
          badge: "滤水帽同类",
          linkLabel: "查看滤水帽",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "不锈钢楔形丝精密滤水帽组件",
          },
        },
        {
          title: "楔形丝筛网总览",
          text: "涵盖平面、弧形、圆筒与管状型材焊接筛网的完整产品大类与选型技术资料。",
          to: "/WedgeWireScreenPage",
          badge: "所属分类",
          linkLabel: "返回楔形丝总览",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
            alt: "不锈钢楔形丝筛网制造工艺与产品总览",
          },
        },
      ],
    },
    cta: {
      id: "sieve-bend-cta",
      eyebrow: "重力分离与筛网选型",
      heading: "咨询弧形筛板技术参数与成套方案",
      lead: "我们的技术支持人员可协助核算脱水通量、确定缝隙公差与弧度半径，并提供成套箱体设计。",
      secondaryLabel: "返回楔形丝总览",
      secondaryTo: "/WedgeWireScreenPage",
      note: "标准不锈钢牌号涵盖 AISI 304 与 AISI 316L。材质证明与出厂尺寸检验报告可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "SieveBendScreens",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Rejillas de alambre en cuña",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Cribas curvas estáticas sieve bend",
      lead: "Paneles curvos de alambre perfilado diseñados para separación por gravedad sin motor, deshidratación rápida y retención precisa de sólidos en líneas de proceso industrial.",
      secondaryCta: {
        label: "Rejillas en cuña general",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
        alt: "Panel de criba curva de acero inoxidable para deshidratación sólido-líquido",
      },
      cardNote: "Aberturas de ranura de 0,25 a 3,0 mm",
      cardLink: {
        label: "Solicitar cotización",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Ancho de ranura", value: "0,25 – 3,0 mm" },
        { label: "Ángulos de arco", value: "45° / 60° / 120°" },
        { label: "Alimentación", value: "Gravedad estática" },
        { label: "Aleaciones", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Separación estática por flujo cruzado",
      heading: "Deshidratación continua por gravedad sin accionamientos mecánicos",
      paragraphs: [
        "Las cribas curvas estáticas (conocidas en la industria como cribas DSM) operan bajo el principio de separación hidrodinámica de flujo cruzado. La suspensión ingresa tangencialmente por la parte superior y se desliza a gran velocidad sobre los alambres perfilados en V perpendiculares al flujo. El filo de cada alambre corta una fina capa de líquido del fondo del flujo, drenándola a través de la ranura ensanchada hacia el interior, mientras los sólidos deshidratados continúan cayendo por la superficie curva.",
        "Al basarse exclusivamente en la energía de la gravedad y la inercia, la criba opera sin motores eléctricos ni componentes vibratorios. Esto elimina el desgaste mecánico, reduce costes de energía y minimiza paradas operativas.",
        "Fabricadas mediante electrosoldadura por resistencia en aceros inoxidables AISI 304 y AISI 316L, en ángulos de arco estándar de 45, 60 y 120 grados, disponibles como paneles sueltos o sistemas completos con caja de alimentación.",
      ],
      keyPoints: [
        "Operación estática por gravedad sin motores mecánicos, reduciendo costes de energía y mantenimiento",
        "Área abierta continua y autolimpiante que drena el líquido por las ranuras mientras los sólidos se deslizan",
        "Paneles de sustitución y conjuntos completos con caja de alimentación, vertedero distribuidor y cubiertas",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
        alt: "Panel de criba curva de acero inoxidable para deshidratación sólido-líquido",
      },
    },
    specTable: {
      heading: "Parámetros técnicos de cribas curvas estáticas",
      intro: "Configuraciones dimensionales estándar para paneles curvos de alambre en cuña.",
      columns: [
        { key: "slot", label: "Ranura (mm)" },
        { key: "arcAngle", label: "Ángulo de arco", align: "right" },
        { key: "radius", label: "Radio nominal (mm)", align: "right" },
        { key: "width", label: "Ancho del panel (mm)", align: "right" },
        { key: "metallurgy", label: "Aleaciones estándar", align: "right" },
      ],
      rows: [
        { slot: "0,25 mm", arcAngle: "45° / 60°", radius: "1016 mm", width: "300 – 1500 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "0,50 mm", arcAngle: "45° / 60°", radius: "1016 mm", width: "300 – 2000 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "0,75 mm", arcAngle: "45° / 60° / 120°", radius: "1016 mm / 1524 mm", width: "500 – 2000 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "1,00 mm", arcAngle: "45° / 60° / 120°", radius: "1016 mm / 1524 mm", width: "500 – 2200 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "1,50 mm", arcAngle: "45° / 60°", radius: "1524 mm", width: "600 – 2400 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "2,00 mm", arcAngle: "45° / 60°", radius: "1524 mm", width: "600 – 2400 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "3,00 mm", arcAngle: "45° / 60°", radius: "1524 mm / 2032 mm", width: "600 – 2500 mm", metallurgy: "AISI 304 / 316L" },
      ],
      caption: "Radios especiales, sistemas de sujeción lateral y cajas de distribución a medida según pedido.",
    },
    features: {
      heading: "Características de diseño y fabricación",
      intro: "Ventajas constructivas de los paneles curvos electrosoldados MutualFilter.",
      items: [
        {
          title: "Drenaje por corte hidrodinámico",
          text: "Los alambres perfilados perpendiculares al flujo cortan láminas de agua sin obstruir las aberturas.",
        },
        {
          title: "Alivio interior antiobstrucción",
          text: "El perfil en V ensancha hacia el interior, permitiendo que las partículas pasantes se liberen inmediatamente.",
        },
        {
          title: "Soldadura por resistencia en cada punto",
          text: "Fusión continua con las varillas de soporte asegurando rigidez para soportar cargas elevadas.",
        },
        {
          title: "Instalación reversible 180°",
          text: "Diseño simétrico que permite girar el panel para compensar el desgaste de la zona de impacto.",
        },
      ],
    },
    related: {
      heading: "Gama relacionada de rejillas en cuña",
      intro: "Compare cribas curvas con paneles planos, cilindros y tubos de ranura continua.",
      cards: [
        {
          title: "Cribas curvas",
          text: "Paneles conformados a radios específicos para bastidores vibratorios y fondos curvos.",
          to: "/CurvedScreensPage",
          badge: "Variante curva",
          linkLabel: "Ver cribas curvas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
            alt: "Panel de criba curva de acero inoxidable",
          },
        },
        {
          title: "Paneles planos",
          text: "Paneles planos con marco perimetral para cribas vibratorias y lechos de drenaje.",
          to: "/FlatPanelScreensPage",
          badge: "Variante plana",
          linkLabel: "Ver paneles planos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "Panel plano de rejilla de alambre en cuña",
          },
        },
        {
          title: "Cilindros filtrantes",
          text: "Tambores cilíndricos de ranura continua para trommels rotativos y filtros industriales.",
          to: "/CylindricalScreensPage",
          badge: "Variante cilíndrica",
          linkLabel: "Ver cilindros",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "Cilindros de rejilla de alambre en cuña",
          },
        },
        {
          title: "Tubos y bujías filtrantes",
          text: "Tubos de ranura continua axial o circunferencial para captación de agua y clarificación.",
          to: "/ScreenTubesPage",
          badge: "Variante tubular",
          linkLabel: "Ver tubos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Tubos de rejilla en cuña para filtración",
          },
        },
        {
          title: "Toberas de filtración",
          text: "Toberas ranuradas para fondos de intercambio iónico, filtros de arena y torres de carbón.",
          to: "/FilterNozzlesPage",
          badge: "Toberas",
          linkLabel: "Ver toberas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "Toberas de acero inoxidable en alambre en cuña",
          },
        },
        {
          title: "Rejillas de alambre en cuña general",
          text: "Gama completa de soluciones de alambre perfilado continuo para filtración y separación.",
          to: "/WedgeWireScreenPage",
          badge: "Categoría principal",
          linkLabel: "Ver gama completa",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
            alt: "Fabricación de rejillas de alambre en cuña",
          },
        },
      ],
    },
    cta: {
      id: "sieve-bend-cta",
      eyebrow: "Ingeniería de separación",
      heading: "Consulte especificaciones de cribas curvas",
      lead: "Consulte a nuestro equipo técnico sobre la abertura adecuada y radios de curvatura para su proceso.",
      secondaryLabel: "Volver a rejillas en cuña",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Aleaciones estándar AISI 304 y AISI 316L. Certificados de material disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "SieveBendScreens",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Spaltsiebe",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Statische Bogensiebe (Sieve Bend)",
      lead: "Gekrümmte Querstrom-Spaltsiebböden für antriebslose Schwerkrafttrennung, zügige Entwässerung und präzisen Partikelrückhalt in industriellen Aufbereitungsprozessen.",
      secondaryCta: {
        label: "Spaltsiebe Übersicht",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
        alt: "Industrielle Edelstahl-Bogensiebplatte zur Schwerkraftentwässerung",
      },
      cardNote: "Spaltweiten von 0,25 bis 3,0 mm",
      cardLink: {
        label: "Angebot anfordern",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Spaltweite", value: "0,25 – 3,0 mm" },
        { label: "Bogenwinkel", value: "45° / 60° / 120°" },
        { label: "Zulaufart", value: "Statischer Schwerkraftfluss" },
        { label: "Werkstoffe", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Statische Querstromtrennung",
      heading: "Kontinuierliche Schwerkraftentwässerung ohne mechanische Antriebe",
      paragraphs: [
        "Statische Bogensiebe (in der Industrie als DSM-Siebe bekannt) nutzen die Hydrodynamik des Querstroms. Die Suspension trifft tangential auf den oberen Einlaufbereich und strömt beschleunigt über die quer liegenden V-förmigen Spaltsiebdrähte. Die Kanten jedes Profils schälen eine dünne Schicht Flüssigkeit ab, die durch den sich nach innen erweiternden Spalt abfließt, während die vorentwässerten Feststoffe über die Biegung nach unten gleiten.",
        "Weil die Trennung rein auf Schwerkraft und Trägheit beruht, benötigt das System keine Motoren oder Vibrationseinheiten. Das verringert mechanischen Verschleiß und senkt Betriebskosten.",
        "Gefertigt im Widerstandsschweißverfahren aus Edelstahl AISI 304 und AISI 316L mit standardmäßigen Bogenwinkeln von 45°, 60° und 120°, lieferbar als Einzelsiebböden oder komplette Gehäuseeinheiten.",
      ],
      keyPoints: [
        "Rein schwerkraftbetriebene statische Trennung ohne Motoren oder Getriebe für minimalen Wartungsaufwand",
        "Kontinuierlich verstopfungsfreie offene Fläche trennt Flüssigkeit ab, während Feststoffe abgleiten",
        "Austauschsiebbeläge sowie komplette Baugruppen mit Einlaufkasten und Spritzschutz lieferbar",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
        alt: "Industrielle Edelstahl-Bogensiebplatte zur Schwerkraftentwässerung",
      },
    },
    specTable: {
      heading: "Technische Parameter statischer Bogensiebe",
      intro: "Standardmaße für gekrümmte Spaltsiebböden aus Edelstahllegierungen.",
      columns: [
        { key: "slot", label: "Spaltweite (mm)" },
        { key: "arcAngle", label: "Bogenwinkel", align: "right" },
        { key: "radius", label: "Nennradius (mm)", align: "right" },
        { key: "width", label: "Siebbreite (mm)", align: "right" },
        { key: "metallurgy", label: "Standardwerkstoffe", align: "right" },
      ],
      rows: [
        { slot: "0,25 mm", arcAngle: "45° / 60°", radius: "1016 mm", width: "300 – 1500 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "0,50 mm", arcAngle: "45° / 60°", radius: "1016 mm", width: "300 – 2000 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "0,75 mm", arcAngle: "45° / 60° / 120°", radius: "1016 mm / 1524 mm", width: "500 – 2000 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "1,00 mm", arcAngle: "45° / 60° / 120°", radius: "1016 mm / 1524 mm", width: "500 – 2200 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "1,50 mm", arcAngle: "45° / 60°", radius: "1524 mm", width: "600 – 2400 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "2,00 mm", arcAngle: "45° / 60°", radius: "1524 mm", width: "600 – 2400 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "3,00 mm", arcAngle: "45° / 60°", radius: "1524 mm / 2032 mm", width: "600 – 2500 mm", metallurgy: "AISI 304 / 316L" },
      ],
      caption: "Sonderradien, Randleisten und Zulaufkästen nach Kundenvorgabe lieferbar.",
    },
    features: {
      heading: "Konstruktionsmerkmale & Fertigung",
      intro: "Konstruktive Vorzüge der widerstandsgeschweißten Bogensiebe von MutualFilter.",
      items: [
        {
          title: "Hydrodynamische Rillentrennung",
          text: "Quer verlaufende Profilkanten trennen Flüssigkeitsfilme wirksam ab, ohne die Spalte zu verstopfen.",
        },
        {
          title: "Selbstreinigendes Spaltprofil",
          text: "Das V-Profil erweitert sich nach innen, sodass durchdringende Partikel ungehindert passieren.",
        },
        {
          title: "Vollverschweißte Kreuzungspunkte",
          text: "Automatisierte Widerstandsschweißung verleiht hohe Stabilität bei abrasiver Feststoffbelastung.",
        },
        {
          title: "Umkehrbare Montage (180°)",
          text: "Symmetrische Siebböden können gewendet werden, um den Verschleiß am Einlauf auszugleichen.",
        },
      ],
    },
    related: {
      heading: "Verwandte Spaltsieb-Bauformen",
      intro: "Vergleichen Sie Bogensiebe mit Flachsieben, Zylindern und Spaltsiebrohren.",
      cards: [
        {
          title: "Gekrümmte Siebe",
          text: "Auf vorgegebene Radien gerollte Spaltsiebe für Vibrationsrahmen und gewölbte Wannen.",
          to: "/CurvedScreensPage",
          badge: "Bogenbauform",
          linkLabel: "Gekrümmte Siebe ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
            alt: "Gekrümmter Spaltsiebboden",
          },
        },
        {
          title: "Flache Spaltsiebböden",
          text: "Eingefasste Flachsiebböden für Schwingmaschinen und stationäre Entwässerungsbecken.",
          to: "/FlatPanelScreensPage",
          badge: "Flachbauform",
          linkLabel: "Flachsiebe ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "Flacher Spaltsiebboden mit Rahmen",
          },
        },
        {
          title: "Spaltsiebzylinder",
          text: "Endlos geschweißte Spaltsiebzylinder für rotierende Trommeln und industrielle Trennsysteme.",
          to: "/CylindricalScreensPage",
          badge: "Zylinderbauform",
          linkLabel: "Zylinder ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "Industrielle Spaltsiebzylinder",
          },
        },
        {
          title: "Spaltsiebrohre",
          text: "Rohrelemente mit axialen oder radialen Spalten für Brunnenbau und Prozessfiltration.",
          to: "/ScreenTubesPage",
          badge: "Rohrbauform",
          linkLabel: "Rohre ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Edelstahl-Spaltsiebrohre",
          },
        },
        {
          title: "Filterdüsen",
          text: "Spaltsiebdüsen für Ionenaustauscher, Sandfilteranlagen und Aktivkohlebehälter.",
          to: "/FilterNozzlesPage",
          badge: "Düsen",
          linkLabel: "Düsen ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "Edelstahl-Filterdüsen",
          },
        },
        {
          title: "Spaltsiebe Gesamtübersicht",
          text: "Umfassende Übersicht aller Spaltsieb-Produktfamilien für Filtration und Entwässerung.",
          to: "/WedgeWireScreenPage",
          badge: "Hauptkategorie",
          linkLabel: "Zur Übersicht",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
            alt: "Spaltsiebe Fertigung und Übersicht",
          },
        },
      ],
    },
    cta: {
      id: "sieve-bend-cta",
      eyebrow: "Verfahrenstechnische Auslegung",
      heading: "Bogensiebe anfragen",
      lead: "Unser technischer Vertrieb unterstützt Sie bei Spaltweite, Bogenmaß und Gehäuseauslegung.",
      secondaryLabel: "Zurück zu Spaltsiebe",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Standardlegierungen AISI 304 und AISI 316L. Werksprüfzeugnisse auf Kundenanforderung lieferbar.",
    },
  },
  fr: {
    breadcrumbKey: "SieveBendScreens",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Grilles à fils profilés",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Grilles courbes statiques sieve bend",
      lead: "Panneaux courbes en fils profilés conçus pour la séparation gravitaire sans énergie, l'égouttage rapide et la rétention fiable des matières solides en milieu industriel.",
      secondaryCta: {
        label: "Aperçu grilles en fil",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
        alt: "Panneau de grille courbe en acier inoxydable pour séparation gravitaire",
      },
      cardNote: "Ouvertures de fente de 0,25 à 3,0 mm",
      cardLink: {
        label: "Demander un devis",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Largeur de fente", value: "0,25 – 3,0 mm" },
        { label: "Angles d'arc", value: "45° / 60° / 120°" },
        { label: "Alimentation", value: "Gravité statique" },
        { label: "Nuances d'alliage", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Séparation statique à flux transversal",
      heading: "Égouttage continu par gravité sans motorisation mécanique",
      paragraphs: [
        "Les grilles courbes statiques (couramment appelées tamis DSM) fonctionnent sur le principe de l'écoulement transversal. La suspension fluide est introduite tangentiellement au sommet et glisse le long de la courbure à travers des fils profilés en V perpendiculaires au flux. L'arête supérieure de chaque fil coupe une fine pellicule de liquide qui s'écoule par les fentes, tandis que les solides essorés glissent vers le bas.",
        "Reposant uniquement sur la gravité et l'inertie du produit, ce système fonctionne sans moteur ni vibration. Cela supprime les organes d'usure en rotation et réduit les coûts d'exploitation et de maintenance.",
        "Fabriquées par soudure électrique par résistance en aciers inoxydables AISI 304 et AISI 316L avec des angles d'arc de 45°, 60° et 120°, disponibles en panneaux interchangeables ou caissons équipés de déversoir.",
      ],
      keyPoints: [
        "Fonctionnement statique gravitaire sans motorisation, réduisant la consommation électrique et l'entretien",
        "Surface ouverte continue anti-colmatage drainant le liquide tandis que les solides glissent",
        "Panneaux de remplacement et caissons complets avec boîte d'alimentation et capots",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
        alt: "Panneau de grille courbe en acier inoxydable pour séparation gravitaire",
      },
    },
    specTable: {
      heading: "Paramètres types des grilles courbes statiques",
      intro: "Dimensions courantes pour panneaux courbes en profilé inox.",
      columns: [
        { key: "slot", label: "Fente (mm)" },
        { key: "arcAngle", label: "Angle d'arc", align: "right" },
        { key: "radius", label: "Rayon nominal (mm)", align: "right" },
        { key: "width", label: "Largeur de grille (mm)", align: "right" },
        { key: "metallurgy", label: "Nuances d'acier", align: "right" },
      ],
      rows: [
        { slot: "0,25 mm", arcAngle: "45° / 60°", radius: "1016 mm", width: "300 – 1500 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "0,50 mm", arcAngle: "45° / 60°", radius: "1016 mm", width: "300 – 2000 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "0,75 mm", arcAngle: "45° / 60° / 120°", radius: "1016 mm / 1524 mm", width: "500 – 2000 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "1,00 mm", arcAngle: "45° / 60° / 120°", radius: "1016 mm / 1524 mm", width: "500 – 2200 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "1,50 mm", arcAngle: "45° / 60°", radius: "1524 mm", width: "600 – 2400 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "2,00 mm", arcAngle: "45° / 60°", radius: "1524 mm", width: "600 – 2400 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "3,00 mm", arcAngle: "45° / 60°", radius: "1524 mm / 2032 mm", width: "600 – 2500 mm", metallurgy: "AISI 304 / 316L" },
      ],
      caption: "Rayons spéciaux, barres de serrage et caissons de distribution sur demande.",
    },
    features: {
      heading: "Caractéristiques de conception et réalisation",
      intro: "Avantages techniques des grilles courbes électrosoudées MutualFilter.",
      items: [
        {
          title: "Cisaillement hydrodynamique",
          text: "Fils profilés disposés perpendiculairement au flux cisaillant les lames liquides sans aveugler la fente.",
        },
        {
          title: "Fentes évasées anti-colmatage",
          text: "Le profil en V s'élargit vers l'aval pour laisser échapper les particules calibrées sans coincement.",
        },
        {
          title: "Soudure par résistance intégrale",
          text: "Fusion complète à chaque intersection assurant une rigidité maximale sous charges d'effluents lourds.",
        },
        {
          title: "Montage réversible à 180°",
          text: "Panneaux symétriques réversibles pour répartir l'usure de la zone d'impact supérieure.",
        },
      ],
    },
    related: {
      heading: "Gamme de grilles en fil profilé",
      intro: "Comparez les grilles courbes avec les panneaux plans, les cylindres et les tubes de filtration.",
      cards: [
        {
          title: "Grilles courbes",
          text: "Panneaux cintrés selon des rayons spécifiques pour cribles vibrants et fonds courbes.",
          to: "/CurvedScreensPage",
          badge: "Variante courbe",
          linkLabel: "Voir grilles courbes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
            alt: "Panneau de grille courbe",
          },
        },
        {
          title: "Panneaux plans",
          text: "Grilles planes avec encadrement pour cribles vibrants et égoutteurs statiques.",
          to: "/FlatPanelScreensPage",
          badge: "Variante plane",
          linkLabel: "Voir panneaux plans",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "Panneau plan en fil profilé",
          },
        },
        {
          title: "Cylindres filtrants",
          text: "Cylindres soudés à fente continue pour trommels et installations de clarification.",
          to: "/CylindricalScreensPage",
          badge: "Variante cylindrique",
          linkLabel: "Voir cylindres",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "Cylindres en fil profilé",
          },
        },
        {
          title: "Tubes et crépines",
          text: "Tubes à fente continue axiale ou radiale pour forages d'eau et procédés industriels.",
          to: "/ScreenTubesPage",
          badge: "Variante tubulaire",
          linkLabel: "Voir tubes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Tubes et crépines de filtration",
          },
        },
        {
          title: "Crépines de fond (buses)",
          text: "Crépines à fentes pour colonnes d'échange d'ions, filtres à sable et charbon actif.",
          to: "/FilterNozzlesPage",
          badge: "Crépines",
          linkLabel: "Voir crépines",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "Crépines en fil profilé",
          },
        },
        {
          title: "Aperçu général grilles en fil",
          text: "Gamme complète des produits en fil profilé pour la filtration et la séparation.",
          to: "/WedgeWireScreenPage",
          badge: "Catégorie parente",
          linkLabel: "Voir la gamme",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
            alt: "Fabrication de grilles à fils profilés",
          },
        },
      ],
    },
    cta: {
      id: "sieve-bend-cta",
      eyebrow: "Étude & Faisabilité",
      heading: "Demandez une étude pour grille courbe",
      lead: "Nos ingénieurs d'application vous guident pour sélectionner la fente, le rayon et les dimensions appropriés.",
      secondaryLabel: "Retour aux grilles en fil",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Aciers inoxydables standards AISI 304 et AISI 316L. Certificats matière fournis lorsque spécifiés dans la commande.",
    },
  },
  pt: {
    breadcrumbKey: "SieveBendScreens",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Telas de arame em cunha",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Peneiras curvas estáticas sieve bend",
      lead: "Painéis curvos em arame perfilado desenvolvidos para separação estática por gravidade, desaguamento acelerado e retenção precisa de sólidos em linhas industriais.",
      secondaryCta: {
        label: "Visão geral de telas em cunha",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
        alt: "Painel de peneira curva em aço inoxidável para desaguamento sólido-líquido",
      },
      cardNote: "Abertura de fenda de 0,25 a 3,0 mm",
      cardLink: {
        label: "Solicitar cotação",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Largura de fenda", value: "0,25 – 3,0 mm" },
        { label: "Ângulos de arco", value: "45° / 60° / 120°" },
        { label: "Alimentação", value: "Fluxo por gravidade estático" },
        { label: "Ligas de aço", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Separação estática por fluxo cruzado",
      heading: "Desaguamento contínuo por gravidade sem acionamento mecânico",
      paragraphs: [
        "As peneiras curvas estáticas (conhecidas na indústria como peneiras DSM) operam sob princípios de separação hidrodinâmica em fluxo cruzado. A polpa ou suspensão entra tangencialmente pela borda superior e acelera ao descer sobre os arames perfilados em V perpendiculares ao fluxo. A borda de ataque de cada arame separa uma fina lâmina de líquido do fundo do fluxo, drenando-a através da fenda com alívio interno, enquanto os sólidos desaguados continuam deslizando pela face côncava.",
        "Como a separação depende unicamente da gravidade e da inércia, a peneira opera sem motores elétricos, engrenagens ou mecanismos vibratórios. Isso elimina o desgaste mecânico e reduz os custos com energia e manutenção.",
        "Fabricadas por solda por resistência elétrica em aços inoxidáveis AISI 304 e AISI 316L, com ângulos de arco padrão de 45, 60 e 120 graus, disponíveis em painéis de reposição ou caixas completas com vertedouro e tampas.",
      ],
      keyPoints: [
        "Operação estática por gravidade sem motores mecânicos, minimizando consumo de energia e manutenção",
        "Área aberta contínua e autolimpante que extrai o fluido através das fendas enquanto os sólidos deslizam",
        "Painéis de reposição e carcaças completas com caixa de alimentação, vertedouro e proteções",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
        alt: "Painel de peneira curva em aço inoxidável para desaguamento sólido-líquido",
      },
    },
    specTable: {
      heading: "Parâmetros técnicos de peneiras curvas estáticas",
      intro: "Dimensões de referência para painéis curvos em arame cunha de aço inoxidável.",
      columns: [
        { key: "slot", label: "Fenda (mm)" },
        { key: "arcAngle", label: "Ângulo de arco", align: "right" },
        { key: "radius", label: "Raio nominal (mm)", align: "right" },
        { key: "width", label: "Largura do painel (mm)", align: "right" },
        { key: "metallurgy", label: "Ligas padrão", align: "right" },
      ],
      rows: [
        { slot: "0,25 mm", arcAngle: "45° / 60°", radius: "1016 mm", width: "300 – 1500 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "0,50 mm", arcAngle: "45° / 60°", radius: "1016 mm", width: "300 – 2000 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "0,75 mm", arcAngle: "45° / 60° / 120°", radius: "1016 mm / 1524 mm", width: "500 – 2000 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "1,00 mm", arcAngle: "45° / 60° / 120°", radius: "1016 mm / 1524 mm", width: "500 – 2200 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "1,50 mm", arcAngle: "45° / 60°", radius: "1524 mm", width: "600 – 2400 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "2,00 mm", arcAngle: "45° / 60°", radius: "1524 mm", width: "600 – 2400 mm", metallurgy: "AISI 304 / 316L" },
        { slot: "3,00 mm", arcAngle: "45° / 60°", radius: "1524 mm / 2032 mm", width: "600 – 2500 mm", metallurgy: "AISI 304 / 316L" },
      ],
      caption: "Raios especiais, barras de fixação lateral e caixas de alimentação sob medida.",
    },
    features: {
      heading: "Características construtivas e fabricação",
      intro: "Vantagens dos painéis curvos eletrossoldados da MutualFilter.",
      items: [
        {
          title: "Drenagem por cisalhamento hidrodinâmico",
          text: "Arames perfilados perpendiculares ao fluxo removem lâminas líquidas sem obstrução das fendas.",
        },
        {
          title: "Geometria antientupimento em V",
          text: "O perfil em V com alargamento interno permite liberação imediata das partículas que passam pela fenda.",
        },
        {
          title: "Soldagem por resistência contínua",
          text: "Fusão em cada interseção com as barras de sustentação, assegurando rigidez estrutural.",
        },
        {
          title: "Montagem reversível em 180°",
          text: "Painéis simétricos que podem ser invertidos periodicamente para equilibrar o desgaste no impacto.",
        },
      ],
    },
    related: {
      heading: "Família de telas em arame de cunha",
      intro: "Compare peneiras curvas com painéis planos, cilindros e tubos de fenda contínua.",
      cards: [
        {
          title: "Telas curvas",
          text: "Painéis calandrados conforme raios sob medida para quadros vibratórios e calhas curvas.",
          to: "/CurvedScreensPage",
          badge: "Variante curva",
          linkLabel: "Ver telas curvas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
            alt: "Painel de tela curva em arame cunha",
          },
        },
        {
          title: "Painéis planos",
          text: "Painéis planos com moldura para peneiras vibratórias e fundos de drenagem estáticos.",
          to: "/FlatPanelScreensPage",
          badge: "Variante plana",
          linkLabel: "Ver painéis planos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "Painel plano de tela de arame cunha",
          },
        },
        {
          title: "Cilindros filtrantes",
          text: "Cilindros de fenda contínua para trommels rotativos e filtros industriais.",
          to: "/CylindricalScreensPage",
          badge: "Variante cilíndrica",
          linkLabel: "Ver cilindros",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "Cilindros de tela de arame perfilado",
          },
        },
        {
          title: "Tubos filtrantes e crepinas",
          text: "Tubos com fenda contínua longitudinal ou circunferencial para poços profundos e processos.",
          to: "/ScreenTubesPage",
          badge: "Variante tubular",
          linkLabel: "Ver tubos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Tubos de tela em cunha para filtragem",
          },
        },
        {
          title: "Bocais de filtragem (crepinas)",
          text: "Crepinas para fundos de troca iônica, leitos de areia e colunas de carvão ativado.",
          to: "/FilterNozzlesPage",
          badge: "Crepinas",
          linkLabel: "Ver crepinas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "Crepinas de aço inoxidável em arame cunha",
          },
        },
        {
          title: "Visão geral de telas em cunha",
          text: "Gama completa de telas de arame perfilado soldado para separação e filtragem.",
          to: "/WedgeWireScreenPage",
          badge: "Categoria principal",
          linkLabel: "Ver linha completa",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
            alt: "Fabricação de telas de arame em cunha",
          },
        },
      ],
    },
    cta: {
      id: "sieve-bend-cta",
      eyebrow: "Engenharia de separação",
      heading: "Consulte especificações de peneiras curvas",
      lead: "Nossa equipe técnica ajuda a definir a abertura de fenda e o raio de curvatura ideais para o seu processo.",
      secondaryLabel: "Voltar para telas em cunha",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Ligas padrão AISI 304 e AISI 316L. Certificados de matéria-prima disponíveis quando especificados no pedido.",
    },
  },
};
